<script setup>
/**
 * The 240px menu panel: the links of whichever section the rail has selected.
 *
 * It sits beside the rail rather than in the document flow, so the two shell
 * states differ only by where it is translated to (see `useSidebarMode`):
 *  - FULL: flush against the rail, with a spacer holding 240px open for it
 *  - MINI: translated a full width left, parking it behind the rail
 *
 * The header toggle pins it; from MINI, pointing at the rail also peeks it back
 * in over the content, and hovering the panel itself holds that peek open.
 * Below the shell breakpoint it is an off-canvas drawer with a backdrop, and
 * the mode toggle does not apply.
 */
import { ref, computed, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { provideSidebarContext } from './provider';
import { useBranding } from 'shared/composables/useBranding';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { useSidebarNavigation } from './useSidebarNavigation';
import {
  useSidebarMode,
  setPanelLinkCount,
  PANEL_WIDTH,
  PANEL_PEEK_CLOSE_DELAY,
} from '../rail/useSidebarMode';
import { vOnClickOutside } from '@vueuse/components';

import SidebarGroup from './SidebarGroup.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'closeMobileSidebar',
]);

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

// Navigation items are defined in ./navigation/* - one module per section.
const { menuItems, panelLinkCount, activeProductArea } = useSidebarNavigation();

// Tell the shell how much this section actually has to offer, so the spacer and
// the header toggle can drop out for a section that needs no panel.
watchEffect(() => setPanelLinkCount(panelLinkCount.value));

const { isDesktop, hasPanel, isPanelOpen, isPeekOpen, openPeek, closePeek } =
  useSidebarMode();

const brandName = computed(() => replaceInstallationName(t('RAIL.BRAND_NAME')));

// The settings panel is its own surface, so it announces itself instead of
// the workspace - the way Intercom's settings sidebar leads with "Settings".
const panelHeading = computed(() =>
  activeProductArea.value === 'settings'
    ? t('SIDEBAR.SETTINGS')
    : brandName.value
);

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

// The panel is a fixed 240px now and its groups no longer collapse, so what
// used to be resize and accordion state is constant. The context keys stay
// because the group and leaf components read them.
provideSidebarContext({
  isCollapsed: computed(() => false),
  sidebarWidth: ref(PANEL_WIDTH),
  isResizing: ref(false),
});

const isVisible = computed(() =>
  isDesktop.value ? isPanelOpen.value : props.isMobileSidebarOpen
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};
</script>

<template>
  <!-- The scrim starts after the rail: the rail stays visible and tappable so
       the open drawer can switch sections. -->
  <div
    v-if="isMobileSidebarOpen"
    aria-hidden="true"
    class="fixed inset-y-0 ltr:left-12 ltr:right-0 rtl:left-0 rtl:right-12 z-40 bg-n-slate-12/30 backdrop-blur-[1px] shell:hidden"
    @click="closeMobileSidebar"
  />

  <aside
    v-if="!isDesktop || hasPanel"
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#product-rail',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    :inert="!isVisible"
    class="fixed top-0 ltr:left-12 rtl:right-12 z-50 flex h-full w-60 flex-col bg-n-background text-sm ltr:border-r rtl:border-l border-n-weak transition-transform duration-300 ease-out motion-reduce:transition-none shell:absolute shell:z-30 shell:ltr:left-20 shell:rtl:right-20"
    :class="[
      isVisible
        ? 'translate-x-0'
        : 'ltr:-translate-x-[calc(100%+3rem)] rtl:translate-x-[calc(100%+3rem)]',
      isMobileSidebarOpen ? 'shadow-lg shell:shadow-none' : '',
      // A peek floats over the conversation list instead of holding a column
      // open, so it needs the shadow to read as the layer above it.
      isPeekOpen ? 'shell:shadow-lg' : '',
    ]"
    @mouseenter="openPeek"
    @mouseleave="closePeek(PANEL_PEEK_CLOSE_DELAY)"
  >
    <div class="flex h-12 flex-shrink-0 items-center px-5">
      <span
        class="truncate font-interDisplay text-base font-semibold tracking-tight text-n-slate-12"
      >
        {{ panelHeading }}
      </span>
    </div>

    <nav
      class="grid flex-grow gap-2 overflow-y-scroll px-2 pb-5 no-scrollbar min-w-0"
    >
      <ul class="m-0 flex list-none flex-col gap-1 min-w-0">
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>

    <section class="relative flex-shrink-0 pb-2">
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
    </section>
  </aside>
</template>
